// ---------------------------------------------------------------------------
// Escopo de PDV — barreira VISUAL das ações (2026-08-22).
//
// O backend autoriza por PDV: expedir/editar/cancelar exigem a ORIGEM,
// conferir exige o DESTINO, tratativa exige QUALQUER PONTA. Quem não tem
// acesso tomava 403 — e o interceptor global levava a `/403` com reload,
// destruindo o formulário já preenchido (uma conferência de 10 itens
// contados). Este módulo antecipa a negativa no botão, com o motivo.
//
// ⚠️ FAIL-OPEN por decisão: a lista de PDVs do usuário chega por HTTP
// (`/users/me/pdvs`) e o hook devolve `[]` quando a requisição FALHA. Derivar
// "não tem acesso" de lista vazia transformaria queda de rede em "você não tem
// permissão" — mentira com cara de regra. Enquanto a lista não carregou com
// sucesso, NADA é bloqueado e a decisão fica com o backend (que continua sendo
// a autoridade real; a barreira daqui é conveniência, não segurança).
//
// PREMISSA: `/users/me/pdvs` devolve o mesmo conjunto que o `injectUserPdvs`
// do backend usa para autorizar. Ambos leem a junção `user_pdvs` — verificado
// nas docs do backend, não no código (fora do alcance de leitura). Se um dia
// divergirem, o modo de falha perigoso é o front esconder ação legítima; o
// contrário (front libera, backend nega) cai no toast do guard de request.
// ---------------------------------------------------------------------------

const NO_BLOCKS = Object.freeze({
   isEnforced: false,
   hasOrigin: true,
   hasDest: true,
   blockedForOrigin: null,
   blockedForDest: null,
   blockedForEither: null,
});

function pdvLabel(name, id) {
   return name ?? (id ? `PDV ${id}` : 'desconhecido');
}

/**
 * Deriva o bloqueio visual de uma ordem/remessa a partir dos PDVs do usuário.
 *
 * @param {object}  params
 * @param {Array}   params.userPdvs  lista de `/users/me/pdvs`
 * @param {boolean} params.isReady   lista carregada COM SUCESSO (ver fail-open)
 * @param {object}  params.entity    ordem ou remessa (tem origin_/dest_pdv_*)
 * @returns {{isEnforced: boolean, hasOrigin: boolean, hasDest: boolean,
 *           blockedForOrigin: string|null, blockedForDest: string|null,
 *           blockedForEither: string|null}}
 */
export function buildPdvAccess({ userPdvs, isReady, entity }) {
   if (!isReady || !entity) return NO_BLOCKS;

   const ids = new Set((userPdvs ?? []).map((pdv) => String(pdv.id)));
   if (ids.size === 0) return NO_BLOCKS;

   const originName = pdvLabel(entity.origin_pdv_name, entity.origin_pdv_id);
   const destName = pdvLabel(entity.dest_pdv_name, entity.dest_pdv_id);

   const hasOrigin = ids.has(String(entity.origin_pdv_id));
   const hasDest = ids.has(String(entity.dest_pdv_id));

   return {
      isEnforced: true,
      hasOrigin,
      hasDest,
      blockedForOrigin: hasOrigin
         ? null
         : `Você não tem acesso ao PDV de origem ${originName}.`,
      blockedForDest: hasDest
         ? null
         : `Você não tem acesso ao PDV de destino ${destName}.`,
      // Ações que aceitam qualquer ponta (tratativa) só travam sem as DUAS.
      // Na prática é quase inalcançável: o escopo de leitura do backend já
      // exige uma das pontas para o registro sequer aparecer. Fica como
      // fallback honesto — se aparecer, o escopo de leitura mudou.
      blockedForEither:
         hasOrigin || hasDest
            ? null
            : `Você não tem acesso ao PDV de origem ${originName} e nem ao PDV de destino ${destName}.`,
   };
}

export const EMPTY_PDV_ACCESS = NO_BLOCKS;
